import { useCallback, useEffect, useMemo, useState } from "react";

import { Source } from "Constant/source";

import {
    googleSendCode,
    googleSendSpreadSheetId,
    googleSpreadSheetCreate,
} from "helpers/Sources/googleSheets";

import SwalAlertComp from "components/SwalAlert";

import { toSpreadsheetPageOptions } from "../SourceCreation.helpers";

/**
 * Google Spreadsheet flow: OAuth login, choose a spreadsheet, choose its
 * pages, create the source and open field mapping.
 */
const useSpreadsheetSource = ({ sourceType, language, usage, openMapping }) => {
    const [domain, setDomain] = useState("");
    const [isLoginVisible, setIsLoginVisible] = useState(true);
    const [isAuthLoading, setIsAuthLoading] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isCreating, setIsCreating] = useState(false);
    const [hasSpreadsheets, setHasSpreadsheets] = useState(false);
    const [spreadsheets, setSpreadsheets] = useState([]);
    const [sourceId, setSourceId] = useState(null);
    const [selectedSpreadsheet, setSelectedSpreadsheet] = useState(null);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [pagesData, setPagesData] = useState(null);
    const [isPagesLoading, setIsPagesLoading] = useState(false);
    const [selectedPages, setSelectedPages] = useState([]);

    const failedTitle = language?.delete_swal_modal?.failed ?? "Failed";

    const reset = useCallback(() => {
        setDomain("");
        setIsLoginVisible(true);
        setIsAuthLoading(false);
        setIsLoading(false);
        setIsCreating(false);
        setHasSpreadsheets(false);
        setSpreadsheets([]);
        setSourceId(null);
        setSelectedSpreadsheet(null);
        setPagesData(null);
        setIsPagesLoading(false);
        setSelectedPages([]);
    }, []);

    useEffect(() => {
        if (sourceType === Source.spreadsheet) reset();
    }, [sourceType, reset]);

    const pageOptions = useMemo(
        () => toSpreadsheetPageOptions(pagesData?.pages),
        [pagesData?.pages],
    );

    const handleLoginSuccess = async (data) => {
        setIsLoading(true);
        const response = await googleSendCode({
            google_code: data?.google_code,
            source_type: data?.source_type,
            domain: data?.domain,
            usage_types: usage,
        });
        if (response.status) {
            setSpreadsheets(response?.data?.sheets ?? []);
            setSourceId(response?.data?.source_id);
            setDomain("");
            setIsLoginVisible(false);
            setHasSpreadsheets(true);
        } else {
            SwalAlertComp({
                title: failedTitle,
                message: response?.message ?? "",
                icon: "error",
            });
        }
        setIsLoading(false);
    };

    const fetchPages = async (spreadsheetId) => {
        setIsPagesLoading(true);
        try {
            const response = await googleSendSpreadSheetId(
                { source_id: sourceId },
                spreadsheetId,
            );
            if (response.status) {
                setPagesData(response?.data);
            } else {
                SwalAlertComp({
                    title: failedTitle,
                    message: response?.message ?? "",
                    icon: "error",
                });
            }
        } finally {
            setIsPagesLoading(false);
        }
    };

    const handleSpreadsheetRowClick = (id) => {
        const spreadsheet = spreadsheets.find((item) => item.id === id);
        setSelectedSpreadsheet(spreadsheet ?? null);
        setPagesData(null);
        setSelectedPages([]);
        fetchPages(spreadsheet?.id);
    };

    const handlePagesChange = (event, pages) => setSelectedPages(pages);

    const createSource = async () => {
        if (isCreating) return;
        setIsCreating(true);
        try {
            const response = await googleSpreadSheetCreate(
                selectedPages,
                pagesData?.source_id,
                selectedSpreadsheet?.id,
                selectedSpreadsheet?.name,
            );
            if (response.status) {
                openMapping(
                    response?.data?.source,
                    Source.spreadsheet,
                    response?.data?.source_type_id,
                );
            } else {
                SwalAlertComp({
                    title: failedTitle,
                    message: response?.message ?? "",
                    icon: "error",
                });
            }
        } finally {
            setIsCreating(false);
        }
    };

    return {
        domain,
        setDomain,
        isLoginVisible,
        isAuthLoading,
        setIsAuthLoading,
        isLoading,
        isCreating,
        hasSpreadsheets,
        spreadsheets,
        selectedSpreadsheetId: selectedSpreadsheet?.id ?? null,
        rowsPerPage,
        setRowsPerPage,
        hasPages: pagesData?.pages?.length > 0,
        pageOptions,
        isPagesLoading,
        selectedPages,
        handlePagesChange,
        handleLoginSuccess,
        handleSpreadsheetRowClick,
        createSource,
        reset,
    };
};

export default useSpreadsheetSource;
